import React, { useEffect, useRef, useState } from 'react';

import { Icon } from '../../../base/icons';
import { IconArrowDown, IconArrowUp } from '../../../base/icons/svg';
import { TRANSLATION_TARGET_LANGUAGES, getPrimaryLanguageCode } from '../../functions.any';

interface ILanguageItem {
  id: string;
  lang: string;
  selected: boolean;
}

interface ILanguageListProps {
  items: Array<ILanguageItem>;
  onLanguageSelected: (lang: string) => void;
  selectedLanguage: string;
  t: (key: string) => string;
  switchEnabled: boolean;
  language: stringSimilarity;
}

/**
 * A language's name in the UI language, followed by its own name when that differs ("Spanish · Español"), so
 * people can find their language whatever language the UI is in.
 *
 * @param {Function} t - The translate function.
 * @param {string} code - The language code.
 * @returns {string}
 */
function getLanguageLabel(t, code) {
    const name = t(`languages:${code}`);
    let native;

    try {
        native = new Intl.DisplayNames([ code ], { type: 'language' }).of(code);
    } catch {
        // Not a code the browser knows; the translated name alone is fine.
    }

    return native && native.toLowerCase() !== name.toLowerCase() && native !== code
        ? `${name} · ${native.charAt(0).toLocaleUpperCase(code) + native.slice(1)}`
        : name;
}

/**
 * Component that renders the security options dialog.
 *
 * @returns {React$Element<any>}
 */
const LanguageList = ({
    onLanguageSelected,
    t,
    switchEnabled,
    language,
    translationEnabled
}: ILanguageListProps) => {
    const [ isOpen, setIsOpen ] = useState(false);
    const [ searchQuery, setSearchQuery ] = useState('');
    const wrapperRef = useRef(null);
    const searchRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = event => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Focus the search box as soon as the list opens, so the list can be narrowed by typing without a second
    // click, and start every opening from the full list rather than the previous search.
    useEffect(() => {
        if (isOpen) {
            searchRef.current?.focus();
        } else {
            setSearchQuery('');
        }
    }, [ isOpen ]);

    const onLanguageSelectedWrapper = item => {
        onLanguageSelected(item);
        setIsOpen(false);
    };

    const toggleDropdown = () => {
        setIsOpen(prev => !prev);
    };

    // Every language captions can be translated into, whatever is being spoken: the spoken language is recognized
    // per sentence, so the list no longer depends on one conference-wide source language (see functions.any).
    const targetLanguages = TRANSLATION_TARGET_LANGUAGES;

    // Match on the language's displayed name, which is what someone reading the list is actually searching for,
    // and additionally on the raw code so that typing "fr" finds French too.
    const query = searchQuery.trim().toLowerCase();
    const matchingLanguages = query
        ? targetLanguages.filter(item => {
            const name = getLanguageLabel(t, item).toLowerCase();

            return name.includes(query) || item.toLowerCase().includes(query);
        })
        : targetLanguages;

    let translationListItems;

    if (targetLanguages.length === 0) {
        translationListItems = (
            <li className = 'language-list__dropdown-list-item language-list__dropdown-list-item--disabled'>
                { t('languages:not_available') }
            </li>
        );
    } else if (matchingLanguages.length === 0) {
        translationListItems = (
            <li className = 'language-list__dropdown-list-item language-list__dropdown-list-item--disabled'>
                { t('transcribing.noLanguageMatches') }
            </li>
        );
    } else {
        translationListItems = matchingLanguages.map(item => (
            <li
                className = 'language-list__dropdown-list-item'
                key = { item }
                onClick = { () => onLanguageSelectedWrapper(item) }>
                { getLanguageLabel(t, item) }
            </li>
        ));
    }

    return (
        <div className = 'language-list__items-container'>
            <div
                className = 'dropdown-menu1'>
                {
                    switchEnabled && translationEnabled === true && (
                        <div
                            className = 'language-list__dropdown-container'>
                            <span className = 'language-list__label'>{ t('transcribing.captionLanguage') }</span>
                            { translationListItems.length > 0
                                ? <div
                                    className = 'dropdown-min-width language-list__dropdown-wrapper'
                                    ref = { wrapperRef }>
                                    <button
                                        className = 'language-list__dropdown-trigger'
                                        onClick = { toggleDropdown }
                                        type = 'button'>
                                        { typeof language === 'string'
                                            ? getLanguageLabel(t, getPrimaryLanguageCode(language))
                                            : t('transcribing.TranslateTo') }
                                        <Icon src = { isOpen ? IconArrowUp : IconArrowDown } />
                                    </button>
                                    { isOpen && (
                                        <ul
                                            className = 'dropdown-menu-languages language-list__dropdown-list'
                                            style = {{ width: wrapperRef.current?.offsetWidth }}>
                                            <li className = 'language-list__dropdown-search'>
                                                <input
                                                    autoComplete = 'off'
                                                    onChange = { e => setSearchQuery(e.target.value) }
                                                    placeholder = { t('transcribing.chooseLanguage') }
                                                    ref = { searchRef }
                                                    type = 'text'
                                                    value = { searchQuery } />
                                            </li>
                                            { translationListItems }
                                        </ul>
                                    ) }
                                </div> : <span className = 'language-list__font16'>{ t('transcribing.TranslationNotAvailable') }</span> }
                        </div>
                    )
                }
            </div>
        </div>
    );
};


export default LanguageList;
